import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useBeforeUnload } from 'react-router-dom';
import { announceConfirmHydraTransition, subscribeToHydraTransitionAttempt } from '@customerio/hydra-nav-state';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Button,
  ButtonGroup,
  Stack,
  InlineStack,
} from '@customerio/pluma-components/react';

export type NavigationGuardOptions = {
  /**
   * Whether there are unsaved changes to protect
   */
  hasUnsavedChanges: boolean;

  /**
   * Custom message to show in the confirmation modal
   */
  message?: string;

  /**
   * Callback to save changes before navigating away
   * If provided, a "Save and Continue" option will be shown
   */
  onSave?: () => Promise<void> | void;

  /**
   * Callback to handle errors from the save operation
   */
  onError?: (error: unknown) => void;

  /**
   * Callback called when user chooses to discard changes
   */
  onDiscard?: () => void;
};

type NavigationGuardState =
  | (NavigationGuardOptions & {
      /**
       * Whether the navigation guard modal is currently open
       */
      isOpen: true;

      /**
       * Function to proceed with navigation after confirmation
       */
      proceedWithNavigation: () => void;
    })
  | (Partial<NavigationGuardOptions> & {
      /**
       * Whether the navigation guard modal is currently open
       */
      isOpen: false;
    });

const NavigationGuardContext = createContext<{
  registerGuard: (options: NavigationGuardOptions) => void;
  unregisterGuard: () => void;
} | null>(null);

export function useNavigationGuard(options: NavigationGuardOptions) {
  const context = useContext(NavigationGuardContext);

  if (context == null) {
    throw new Error('useNavigationGuard must be used within a NavigationGuardProvider');
  }

  const { hasUnsavedChanges, message, onSave, onError, onDiscard } = options;

  // Browser-level protection for refresh/close
  useBeforeUnload(
    hasUnsavedChanges
      ? (event: BeforeUnloadEvent) => {
          event.preventDefault();
          return;
        }
      : () => {
          // no-op function when not blocking
        },
    { capture: true },
  );

  useEffect(() => {
    context.registerGuard(options);

    return () => {
      context.unregisterGuard();
    };
  }, [context, hasUnsavedChanges, message, onSave, onError, onDiscard]);
}

export function NavigationGuardProvider({ children }: { children?: React.ReactNode }) {
  const [state, setState] = useState<NavigationGuardState>({
    isOpen: false,
  });

  const [isSaving, setIsSaving] = useState(false);
  const currentGuardRef = useRef<NavigationGuardOptions | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Set up the subscription once when the provider mounts
  useEffect(() => {
    unsubscribeRef.current = subscribeToHydraTransitionAttempt(() => {
      const currentGuard = currentGuardRef.current;

      // If no guard is registered or no unsaved changes, immediately confirm
      if (currentGuard == null || !currentGuard.hasUnsavedChanges) {
        announceConfirmHydraTransition();
        return;
      }

      // If modal is already open, don't show another one
      if (state.isOpen) {
        return;
      }

      // Show the modal for unsaved changes
      setState({
        isOpen: true,
        proceedWithNavigation: () => announceConfirmHydraTransition(),
        ...currentGuard,
      });
    });

    return () => {
      if (unsubscribeRef.current != null) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [state.isOpen]);

  const registerGuard = useCallback((options: NavigationGuardOptions) => {
    currentGuardRef.current = options;
  }, []);

  const unregisterGuard = useCallback(() => {
    currentGuardRef.current = null;
  }, []);

  const handleSaveAndContinue = useCallback(async () => {
    if (!state.isOpen || state.onSave == null) {
      return;
    }

    try {
      setIsSaving(true);
      await state.onSave();
      state.proceedWithNavigation();
      setState({ isOpen: false });
    } catch (error) {
      state.onError?.(error);
    } finally {
      setIsSaving(false);
    }
  }, [state]);

  const handleDiscardAndContinue = useCallback(() => {
    if (!state.isOpen) {
      return;
    }

    state.onDiscard?.();
    state.proceedWithNavigation();
    setState({ isOpen: false });
  }, [state]);

  const handleCancel = useCallback(() => {
    setState({ isOpen: false });
    setIsSaving(false);
  }, []);

  // Clear state when modal is closed to prevent flash
  function clearState() {
    setState({
      isOpen: false,
    });
  }

  return (
    <NavigationGuardContext.Provider value={{ registerGuard, unregisterGuard }}>
      <Modal isOpen={state.isOpen} onClose={handleCancel} onCloseComplete={clearState} title="Just a minute!">
        <ModalBody>
          {state.message != null && state.message !== '' ? (
            <Paragraph>{state.message}</Paragraph>
          ) : (
            <Stack gap="200">
              <Paragraph>You have unsaved changes.</Paragraph>
              <Paragraph>Please save your changes or discard them to continue.</Paragraph>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter as={InlineStack} alignBlock="center" alignInline="space-between" gap="200">
          <Button variant="secondary" size="sm" onClick={handleCancel} disabled={isSaving}>
            Go back
          </Button>
          <ButtonGroup groupVariant="spaced">
            <Button variant="secondary" size="sm" isDanger onClick={handleDiscardAndContinue} disabled={isSaving}>
              Discard changes
            </Button>
            {state.onSave != null ? (
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveAndContinue}
                isLoading={isSaving}
                disabled={isSaving}
              >
                Save & Continue
              </Button>
            ) : null}
          </ButtonGroup>
        </ModalFooter>
      </Modal>
      {children}
    </NavigationGuardContext.Provider>
  );
}
